import React, { useEffect, useState } from "react";
import ReviewerActionGate from "../../../components/ReviewerActionGate";
import { systemFolderAccepts } from "./kbHelpers";

// Only one box open at a time across the table.
const OPEN_EVENT = "kb-folder-check-open";

export const folderCheckText = (check) =>
  `This looks like a ${check.suggested} document${check.reason ? ` (${check.reason})` : ""}.`;

// "Wrong folder" (or "Suggested: X" for a file in no folder) with a small box to move it or keep it.
const FolderCheckBadge = ({ item, isReviewer = false, onMove, onKeep }) => {
  const check = item.folder_check;
  const [box, setBox] = useState(null);

  useEffect(() => {
    if (!box) return undefined;
    const close = () => setBox(null);
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("click", close);
    window.addEventListener(OPEN_EVENT, close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener(OPEN_EVENT, close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [box]);

  if (!check) return null;

  const open = (event) => {
    event.stopPropagation();
    if (box) return setBox(null);
    window.dispatchEvent(new Event(OPEN_EVENT));
    const r = event.currentTarget.getBoundingClientRect();
    const up = r.bottom + 190 > window.innerHeight;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - 300));
    return setBox({ left, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
  };

  const run = (action) => {
    setBox(null);
    action?.(item);
  };

  return (
    <>
      {check.wrong_folder ? (
        <button
          type="button"
          onClick={open}
          title={folderCheckText(check)}
          className="inline-flex max-w-full items-center gap-1 px-2 py-0.5 rounded-full border border-[#F2A46B] bg-[#FFF1E6] text-[#9A3412] text-[12px] font-medium font-outfit hover:border-[#C2410C]"
        >
          <span className="truncate">Wrong folder</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={open}
          title={folderCheckText(check)}
          className="max-w-full truncate text-left text-[12px] font-medium font-outfit text-[#A04A97] underline underline-offset-2 hover:text-[#7E3577]"
        >
          Suggested: {check.suggested}
        </button>
      )}
      {box && (
        <div
          role="dialog"
          onClick={(e) => e.stopPropagation()}
          style={{ position: "fixed", top: box.top, bottom: box.bottom, left: box.left }}
          className="z-[1000] w-[290px] p-3 bg-white border border-[#D4D4D8] rounded-lg shadow-[0_8px_24px_rgba(24,16,28,0.16)] flex flex-col gap-2 font-outfit whitespace-normal"
        >
          <p className="m-0 text-[14px] font-semibold text-[#18181B]">
            {check.wrong_folder ? "This may be in the wrong folder" : "Suggested folder"}
          </p>
          <p className="m-0 text-[13px] leading-snug text-[#3F3F46]">{folderCheckText(check)}</p>
          {systemFolderAccepts(check.suggested) && (
            <p className="m-0 text-[12px] leading-snug text-[#71717A]">
              {check.suggested} holds: {systemFolderAccepts(check.suggested)}
            </p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <ReviewerActionGate active={isReviewer} place="bottom">
              <button
                type="button"
                onClick={() => run(onMove)}
                className="px-3 py-1.5 rounded-md bg-[#A04A97] text-white text-[13px] font-medium hover:bg-[#8A3D82]"
              >
                Move to {check.suggested}
              </button>
            </ReviewerActionGate>
            <ReviewerActionGate active={isReviewer} place="bottom">
              <button
                type="button"
                onClick={() => run(onKeep)}
                className="px-3 py-1.5 rounded-md border border-[#D4D4D8] bg-white text-[#27272A] text-[13px] font-medium hover:bg-[#F4F4F5]"
              >
                {check.wrong_folder ? "Keep here" : "Dismiss"}
              </button>
            </ReviewerActionGate>
          </div>
        </div>
      )}
    </>
  );
};

export default FolderCheckBadge;
